import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LifeBuoy, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { forgotPasswordSchema } from "@/lib/support-center";
import SEO from "@/components/SEO";

const ForgotPassword = () => {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = forgotPasswordSchema.safeParse({ email });

    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Enter a valid email address.");
      return;
    }

    setSubmitting(true);
    const { error } = await requestPasswordReset(parsed.data.email);
    setSubmitting(false);

    if (error) {
      toast.error(error.message);
      return;
    }

    setSentTo(parsed.data.email);
    toast.success("Password reset email sent.");
  };

  return (
    <div className="min-h-screen bg-hero px-4 py-10 sm:px-6">
      <SEO
        title="Forgot your password — HeirWise"
        description="Send a secure password reset link to your HeirWise account email so you can get back to learning."
        path="/forgot-password"
      />
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 md:flex-row md:items-center md:justify-between">
        <div className="max-w-xl space-y-5 text-primary-foreground">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground">
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
          <div className="space-y-3">
            <p className="text-sm uppercase tracking-[0.24em] text-primary-foreground/60">HeirWise security</p>
            <h1 className="font-heading text-4xl font-bold leading-tight sm:text-5xl">Reset access without the stress.</h1>
            <p className="max-w-lg text-sm leading-7 text-primary-foreground/75 sm:text-base">
              We’ll send a secure recovery link so you can choose a new password and get back into your financial learning journey.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-3xl border border-primary-foreground/15 bg-primary-foreground/10 p-4 backdrop-blur-md">
              <ShieldCheck className="mb-3 h-5 w-5 text-primary-foreground" />
              <p className="text-sm font-medium">Trusted recovery flow</p>
              <p className="mt-1 text-xs text-primary-foreground/70">Old reset links expire, so only the newest recovery email should be used.</p>
            </div>
            <div className="rounded-3xl border border-primary-foreground/15 bg-primary-foreground/10 p-4 backdrop-blur-md">
              <LifeBuoy className="mb-3 h-5 w-5 text-primary-foreground" />
              <p className="text-sm font-medium">Still stuck?</p>
              <p className="mt-1 text-xs text-primary-foreground/70">Use the Support Center for login problems if the email never arrives.</p>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md rounded-[2rem] border border-primary-foreground/15 bg-card/95 p-6 shadow-lg sm:p-8">
          {sentTo ? (
            <div className="space-y-5 text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                <Mail className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h2 className="font-heading text-2xl font-bold text-foreground">Check your email</h2>
                <p className="text-sm leading-6 text-muted-foreground">
                  We sent a password reset link to <span className="font-medium text-foreground">{sentTo}</span>. Open it on the same device for the smoothest recovery experience.
                </p>
              </div>
              <div className="space-y-3">
                <Button asChild className="w-full">
                  <Link to="/login">Return to sign in</Link>
                </Button>
                <Button variant="outline" className="w-full" onClick={() => setSentTo(null)}>
                  Send another reset email
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground">Forgot password</p>
                <h2 className="font-heading text-2xl font-bold text-foreground">Send a recovery link</h2>
                <p className="text-sm leading-6 text-muted-foreground">
                  Enter the email tied to your HeirWise account and we’ll send a secure password reset email right away.
                </p>
              </div>

              <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Sending reset link..." : "Email me a reset link"}
                </Button>
              </form>

              <div className="mt-6 rounded-2xl border border-border/60 bg-background/60 p-4 text-sm text-muted-foreground">
                Need more help with access? Visit <Link className="font-medium text-primary hover:underline" to="/support?category=login_password_issue">Help & Support</Link> after you sign in, or try a fresh reset email.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
